import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { clearAdminToken, getAdminToken, setAdminToken } from '@/lib/admin-store'
import {
  applyAdminFilters,
  getAdminNavLinks,
  readAdminFiltersFromSearch,
  type AdminFilters,
} from './-admin-utils'
import styles from './admin.module.css'

export const Route = createFileRoute('/admin')({
  component: AdminOverviewRoute,
})

function toRangeDates(range: AdminFilters['range']): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  from.setUTCDate(from.getUTCDate() - (range === '30d' ? 30 : 7))
  return {
    from: from.toISOString(),
    to: to.toISOString(),
  }
}

function AdminOverviewRoute() {
  const [tokenInput, setTokenInput] = useState(getAdminToken)
  const [token, setToken] = useState(getAdminToken)
  const [filters, setFilters] = useState<AdminFilters>(readAdminFiltersFromSearch)
  const [overview, setOverview] = useState<Awaited<ReturnType<typeof api.adminOverview>> | null>(
    null
  )
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const navLinks = useMemo(() => getAdminNavLinks(filters), [filters])

  useEffect(() => {
    const nextFilters = readAdminFiltersFromSearch()
    setFilters(nextFilters)
  }, [])

  useEffect(() => {
    const loadOverview = async (): Promise<void> => {
      if (!token) {
        setOverview(null)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const range = toRangeDates(filters.range)
        const data = await api.adminOverview(token, {
          from: range.from,
          to: range.to,
        })
        setOverview(data)
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load admin overview.')
      } finally {
        setIsLoading(false)
      }
    }

    void loadOverview()
  }, [filters.range, token])

  const handleSaveToken = (): void => {
    const trimmed = tokenInput.trim()
    if (trimmed.length === 0) {
      setError('Admin token is required.')
      return
    }

    setAdminToken(trimmed)
    setToken(trimmed)
    setNotice('Admin token saved.')
    setError(null)
  }

  const handleClearToken = (): void => {
    clearAdminToken()
    setTokenInput('')
    setToken('')
    setOverview(null)
    setNotice('Admin token cleared.')
  }

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <h1 className={styles.title}>Admin Overview</h1>
        <p className={styles.subtitle}>
          Decision-first dashboard with drilldowns by acquisition, auth, users, and promoters.
        </p>
      </header>

      <section className={styles.card}>
        <div className={styles.row}>
          <input
            className={styles.input}
            type="password"
            placeholder="x-admin-token"
            value={tokenInput}
            onChange={(event) => setTokenInput(event.target.value)}
          />
          <button className={styles.button} type="button" onClick={handleSaveToken}>
            Save token
          </button>
          <button className={styles.ghostButton} type="button" onClick={handleClearToken}>
            Clear token
          </button>
        </div>
        <div className={styles.row}>
          <label>
            Range
            <select
              className={styles.select}
              value={filters.range}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  range: event.target.value === '30d' ? '30d' : '7d',
                }))
              }
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </label>
          <label>
            Source
            <input
              className={styles.input}
              value={filters.source}
              onChange={(event) =>
                setFilters((current) => ({ ...current, source: event.target.value }))
              }
              placeholder="promoter, direct, organic"
            />
          </label>
          <label>
            Country
            <input
              className={styles.input}
              value={filters.country}
              onChange={(event) =>
                setFilters((current) => ({ ...current, country: event.target.value }))
              }
              placeholder="US"
            />
          </label>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() => applyAdminFilters(filters)}
          >
            Apply Filters
          </button>
        </div>
        <div className={styles.tabs}>
          {navLinks.map((link) => (
            <a key={link.path} href={link.href} className={styles.tabLink}>
              {link.label}
            </a>
          ))}
        </div>
        {notice && <p className={styles.notice}>{notice}</p>}
        {error && <p className={styles.error}>{error}</p>}
      </section>

      {!token && (
        <section className={styles.card}>
          <p className={styles.emptyState}>
            You don't have admin access. Save a valid admin token to continue.
          </p>
        </section>
      )}

      {token && isLoading && (
        <section className={styles.card}>
          <p className={styles.muted}>Loading overview...</p>
        </section>
      )}

      {token && overview && (
        <section className={styles.card}>
          <h2>KPIs</h2>
          {overview.kpis.signups === 0 && overview.kpis.logins === 0 ? (
            <>
              <p className={styles.emptyState}>No metrics yet for this period.</p>
              <div>
                <button
                  className={styles.secondaryButton}
                  type="button"
                  onClick={() => {
                    const nextFilters: AdminFilters = {
                      ...filters,
                      range: '30d',
                    }
                    setFilters(nextFilters)
                    applyAdminFilters(nextFilters)
                  }}
                >
                  Use Last 30 Days
                </button>
              </div>
            </>
          ) : (
            <div className={styles.grid}>
              <article className={styles.kpi}>
                <p className={styles.kpiLabel}>Visitors</p>
                <p className={styles.kpiValue}>{overview.kpis.visitors}</p>
              </article>
              <article className={styles.kpi}>
                <p className={styles.kpiLabel}>Signups</p>
                <p className={styles.kpiValue}>{overview.kpis.signups}</p>
              </article>
              <article className={styles.kpi}>
                <p className={styles.kpiLabel}>Logins</p>
                <p className={styles.kpiValue}>{overview.kpis.logins}</p>
              </article>
              <article className={styles.kpi}>
                <p className={styles.kpiLabel}>Paid Conversions</p>
                <p className={styles.kpiValue}>{overview.kpis.paidConversions}</p>
              </article>
              <article className={styles.kpi}>
                <p className={styles.kpiLabel}>MRR</p>
                <p className={styles.kpiValue}>${overview.kpis.mrr.toFixed(2)}</p>
              </article>
            </div>
          )}
        </section>
      )}

      {token && overview && overview.alerts.length > 0 && (
        <section className={styles.card}>
          <h2>Alerts</h2>
          {overview.alerts.map((alert) => (
            <p key={alert.id}>
              {alert.severity.toUpperCase()} - {alert.message}
            </p>
          ))}
        </section>
      )}
    </section>
  )
}
